<template>
  <h1>Measurement Definitions</h1>
  <v-table v-if="data && data?.data.length">
    <thead>
      <tr>
        <th class="text-left">Name</th>
        <th class="text-left">Unit</th>
        <th class="text-left">Precision</th>
        <th class="text-left">Step</th>
        <th class="text-left">Min</th>
        <th class="text-left">Max</th>
        <th class="text-left">Added</th>
        <th class="text=left">Latest</th>
      </tr>
    </thead>
    <tbody>
      <tr v-for="d in data?.data" :key="d.definition.name">
        <th>{{ d.definition.name }}</th>
        <td>{{ d.definition.unit }}</td>
        <td class="text-right">{{ d.definition.exponent }}</td>
        <td class="text-right">{{ m(d, d.definition.step) }}</td>
        <td class="text-right">{{ m(d, d.definition.minimum) }}</td>
        <td class="text-right">{{ m(d, d.definition.maximum) }}</td>
        <td>{{ reltime(uuidV7ToTimestamp(d.definition.id)) }}</td>
        <td>{{ m(d, d.latest?.significand) }}</td>
      </tr>
    </tbody>
  </v-table>
  <h2>New measurement definition</h2>
  <form @submit="add">
    <v-text-field v-model="name" density="compact" label="name" required />
    <v-select
      v-model="unit"
      :items="Object.values(Unit)"
      label="Unit"
      required
    />
    <v-number-input
      v-model="exponent"
      density="compact"
      label="precision"
      :max="255"
      :min="0"
    />
    <v-number-input
      v-model="step"
      density="compact"
      label="step size"
      :min="Math.pow(10, -exponent)"
      :precision="exponent"
      :step="Math.pow(10, -exponent)"
    />
    <v-number-input
      v-model="min"
      density="compact"
      label="min"
      :precision="exponent"
      :step="Math.pow(10, -exponent)"
      v-bind="{ ...(max !== null && { max }) }"
    />
    <v-number-input
      v-model="max"
      density="compact"
      label="max"
      :precision="exponent"
      :step="Math.pow(10, -exponent)"
      v-bind="{ ...(min !== null && { min }) }"
    />
    <v-btn type="submit">submit</v-btn>
  </form>
</template>
<script setup lang="ts">
import { useQueryClient } from "@tanstack/vue-query";
import {
  type MeasurementDefinition,
  type MeasurementDefinitionWithLatestMeasurement,
  Unit,
  useMeasurementDefinitionsCreate,
  useMeasurementDefinitionsLatest,
} from "../gen/observations-vue";
import { ref, type Ref } from "vue";
import { v7 as uuidv7 } from "uuid";
import { reltime, uuidV7ToTimestamp } from "../utils";

const name = ref("");
const exponent = ref(0);
const step: Ref<null | number> = ref(null);
const min: Ref<null | number> = ref(null);
const max: Ref<null | number> = ref(null);
const unit = ref(Unit.Meter);

const { data, queryKey } = useMeasurementDefinitionsLatest({
  query: { placeholderData: (prev) => prev },
});

function m(
  d: MeasurementDefinitionWithLatestMeasurement,
  v: number | null | undefined,
) {
  if (v === undefined || v === null) {
    return "";
  }
  return v * Math.pow(10, -d.definition.exponent);
}

const { mutate: create } = useMeasurementDefinitionsCreate({
  mutation: {
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey });
    },
  },
});

const queryClient = useQueryClient();
function add() {
  if (name.value) {
    const d: MeasurementDefinition = {
      id: uuidv7(),
      name: name.value,
      unit: unit.value,
      exponent: exponent.value,
    };
    const multiplier = Math.pow(10, exponent.value);
    if (step.value !== null) {
      d.step = step.value * multiplier;
    }
    if (min.value !== null) {
      d.minimum = min.value * multiplier;
    }
    if (max.value !== null) {
      d.maximum = max.value * multiplier;
    }
    create({ data: d });
    name.value = "";
  }
}
</script>
